import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { CheckCircle, XCircle, CircleNotch, Play, X } from '@phosphor-icons/react';

const AtsIngestForm = ({ onDone }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!url) return;
    setBusy(true);
    try {
      const { data } = await api.post('/ingestion/ats', { url });
      toast.success(`${data.detected.ats}: +${data.added} jobs (${data.skipped} skipped)`);
      setUrl('');
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ATS ingest failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-stretch gap-2 w-full sm:w-auto">
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste Greenhouse / Lever / Ashby URL"
        className="rounded-none h-10 w-full sm:w-[320px]"
        data-testid="ats-url"
      />
      <Button onClick={run} disabled={busy || !url} className="rounded-none h-10" data-testid="ats-ingest">
        {busy ? '...' : 'Detect & ingest'}
      </Button>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [rfs, setRfs] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    api.get('/admin/stats').then((r) => setStats(r.data));
    api.get('/role-families').then((r) => setRfs(r.data));
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try { await api.post('/admin/seed'); toast.success('Seeds run'); load(); }
    catch { toast.error('Seed failed'); }
    finally { setSeeding(false); }
  };

  const [ingesting, setIngesting] = useState(false);
  const ingest = async () => {
    setIngesting(true);
    try {
      const { data } = await api.post('/ingestion/run');
      const summary = Object.entries(data.ingested || {}).map(([k, v]) => `${k}: +${v.added}`).join(' · ');
      toast.success(`Ingested — ${summary}`);
      load();
    } catch { toast.error('Ingestion failed'); }
    finally { setIngesting(false); }
  };

  const [smokeRunning, setSmokeRunning] = useState(false);
  const [smokeResult, setSmokeResult] = useState(null);
  const runSmoke = async () => {
    setSmokeRunning(true);
    setSmokeResult(null);
    try {
      const { data } = await api.post('/admin/smoke/run');
      setSmokeResult(data);
      if (data.overall === 'ok') toast.success(`Smoke test ${data.overall.toUpperCase()} — ${data.duration_ms}ms`);
      else toast.warning(`Smoke test ${data.overall.toUpperCase()} — see details`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Smoke test crashed');
    } finally {
      setSmokeRunning(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">ADMIN</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-8">System health & taxonomy.</h1>

        <div className="grid md:grid-cols-3 gap-0 grid-skeleton mb-10">
          {stats && Object.entries(stats).map(([k, v]) => (
            <div key={k} className="p-6 bg-white" data-testid={`stat-${k}`}>
              <div className="label-eyebrow text-muted-foreground">{k.replaceAll('_', ' ')}</div>
              <div className="mono font-black text-5xl mt-2">{v}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={seed} disabled={seeding} variant="outline" className="rounded-none" data-testid="run-seed">{seeding ? 'Seeding...' : 'Re-run seeds + demo users'}</Button>
          <Button onClick={ingest} disabled={ingesting} className="rounded-none" data-testid="run-ingest">{ingesting ? 'Ingesting...' : 'Ingest jobs (Job Bank + ALIS + 80K)'}</Button>
          <AtsIngestForm onDone={load} />
          <Link to="/admin/fairness">
            <Button variant="outline" className="rounded-none" data-testid="goto-fairness">Fairness audit →</Button>
          </Link>
          <Link to="/admin/discover">
            <Button variant="outline" className="rounded-none" data-testid="goto-discover">Discover jobs →</Button>
          </Link>
          <Link to="/admin/role-families">
            <Button variant="outline" className="rounded-none" data-testid="goto-role-families">Role families →</Button>
          </Link>
          <Link to="/admin/skills-vocabulary">
            <Button variant="outline" className="rounded-none" data-testid="goto-skills-vocab">Skills vocabulary →</Button>
          </Link>
          <Button
            onClick={runSmoke}
            disabled={smokeRunning}
            className="rounded-none ml-auto"
            data-testid="run-smoke-test"
          >
            {smokeRunning ? (<><CircleNotch size={14} className="mr-1.5 animate-spin" />Running end-to-end…</>) : (<><Play size={14} weight="fill" className="mr-1.5" />Run smoke test</>)}
          </Button>
        </div>

        {smokeResult && (
          <SmokeResultPanel result={smokeResult} onClose={() => setSmokeResult(null)} />
        )}

        <div className="label-eyebrow text-muted-foreground mb-3">ROLE FAMILIES</div>
        <div className="border border-border">
          {rfs.map((f) => (
            <div key={f.id} className="p-4 bg-white border-b border-border" data-testid={`rf-${f.slug}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-black text-lg">{f.name}</div>
                  <div className="text-xs text-muted-foreground">{f.description}</div>
                </div>
                <div className="mono text-xs text-muted-foreground">
                  perf {f.scoring_weights.performance} · cons {f.scoring_weights.consistency} · rel {f.scoring_weights.reliability} · art {f.scoring_weights.artifacts}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

export default AdminDashboard;

// ─── Smoke test result panel ─────────────────────────────────────────────────

const STEP_LABEL = {
  pick_job: 'Pick a real job',
  create_candidate: 'Create synthetic candidate',
  pick_assessment: 'Load an assessment',
  submit_attempt: 'Submit answers',
  apply_to_job: 'Apply to job',
  compute_score: 'Compute score (LLM)',
  employer_invite: 'Send invite email (Resend)',
  cleanup: 'Clean up test data',
};

const SmokeResultPanel = ({ result, onClose }) => {
  const overallClass = result.overall === 'ok'
    ? 'bg-primary/10 border-primary/40'
    : result.overall === 'partial' ? 'bg-amber-50 border-amber-300' : 'bg-destructive/10 border-destructive/40';
  return (
    <div className={`border-2 ${overallClass} p-5 mb-6`} data-testid="smoke-result">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-eyebrow text-muted-foreground mb-1">SMOKE TEST RESULT</div>
          <div className="font-display font-black text-2xl">
            {result.overall === 'ok' ? '✅ All systems green' : result.overall === 'partial' ? '⚠️ Partial — some steps failed' : '❌ Pipeline broken'}
          </div>
          <div className="mono text-xs text-muted-foreground mt-1">
            {result.duration_ms}ms total · {result.cleaned_up ? 'test data cleaned up' : 'test data NOT cleaned'}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-secondary" data-testid="smoke-close"><X size={16} /></button>
      </div>
      <div className="space-y-1.5 mt-4">
        {result.steps.map((s, i) => {
          const icon = s.status === 'ok' ? <CheckCircle size={16} weight="fill" className="text-primary shrink-0" /> : s.status === 'fail' ? <XCircle size={16} weight="fill" className="text-destructive shrink-0" /> : <CircleNotch size={16} className="text-muted-foreground shrink-0" />;
          return (
            <div key={i} className="flex items-start gap-3 bg-white border border-border p-2.5" data-testid={`smoke-step-${s.name}`}>
              {icon}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{STEP_LABEL[s.name] || s.name}</span>
                  <span className="mono text-xs text-muted-foreground">{s.duration_ms}ms</span>
                </div>
                {s.detail && <div className="text-xs text-destructive mt-1">{s.detail}</div>}
                {s.data && Object.keys(s.data).length > 0 && (
                  <div className="mono text-[10px] text-muted-foreground mt-1 line-clamp-2 break-all">
                    {Object.entries(s.data).slice(0, 4).map(([k, v]) => (
                      <span key={k} className="mr-3">{k}: {typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80)}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
